import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { ClockIcon, SignalIcon } from '@heroicons/react/24/outline';

export interface AlertsPanelProps {
  sosEvents: any[];
  alerts: any[];
  notifications: any[];
  geofenceEvents: any[];
  hideHeader?: boolean;
}

/** Total shown in the panel header and the mobile chip badge. */
export function alertsPanelCount(p: Pick<AlertsPanelProps, 'sosEvents' | 'alerts' | 'notifications' | 'geofenceEvents'>) {
  return (
    p.sosEvents.filter((s) => s?.status && ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(s.status)).length +
    p.alerts.filter((a) => a?.status === 'ACTIVE').length +
    p.notifications.filter((n) => !n.read).length +
    p.geofenceEvents.length
  );
}

/**
 * Live alerts feed (SOS, alerts, messages, geofence entries/exits).
 * Lives in the desktop right column and the mobile "Alerts" bottom sheet.
 */
export default function AlertsPanel({ sosEvents, alerts, notifications, geofenceEvents, hideHeader }: AlertsPanelProps) {
  const total = alertsPanelCount({ sosEvents, alerts, notifications, geofenceEvents });

  return (
    <div className="flex flex-col h-full min-h-0">
      {!hideHeader && (
        <div className="p-4 border-b border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">Alerts</h2>
          <p className="text-sm text-gray-400">{total} total</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {hideHeader && <p className="px-1 text-xs text-gray-500">{total} total</p>}

        {/* SOS Events */}
        {sosEvents
          .filter((sos) => sos?.status && ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING', 'RESOLVED'].includes(sos.status))
          .sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime())
          .slice(0, 20)
          .map((sos) => {
            const isResolved = sos?.status === 'RESOLVED';
            return (
              <div
                key={sos.id}
                className={clsx(
                  'p-3 rounded-lg border',
                  isResolved ? 'bg-gray-800/30 border-gray-700' : 'bg-red-500/10 border-red-500 animate-pulse'
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded font-bold">SOS</span>
                      <span
                        className={clsx(
                          'px-2 py-0.5 text-xs rounded font-semibold',
                          isResolved
                            ? 'bg-green-600/20 text-green-400 border border-green-600'
                            : 'bg-yellow-600/20 text-yellow-400 border border-yellow-600'
                        )}
                      >
                        {isResolved ? '✓ Resolved' : sos.status}
                      </span>
                    </div>
                    <p className="text-white text-sm font-semibold mt-2">{sos.platoon?.callsign || 'Unknown platoon'}</p>
                    {sos.platoon?.unit?.code && <p className="text-gray-400 text-xs">{sos.platoon.unit.code}</p>}
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <ClockIcon className="w-3 h-3" />
                      {formatDistanceToNow(new Date(sos.triggeredAt), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

        {/* General Alerts */}
        {alerts
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 20)
          .map((alert) => {
            const isResolved = alert.status === 'RESOLVED';
            const severityColors: Record<string, string> = {
              CRITICAL: 'bg-red-500/10 border-red-500',
              HIGH: 'bg-orange-500/10 border-orange-500',
              MEDIUM: 'bg-amber-500/10 border-amber-500',
              LOW: 'bg-blue-500/10 border-blue-500',
              INFO: 'bg-gray-500/10 border-gray-500',
            };
            const severityBadgeColors: Record<string, string> = {
              CRITICAL: 'bg-red-500 text-white',
              HIGH: 'bg-orange-500 text-white',
              MEDIUM: 'bg-amber-500 text-black',
              LOW: 'bg-blue-500 text-white',
              INFO: 'bg-gray-500 text-white',
            };

            return (
              <div
                key={alert.id}
                className={clsx(
                  'p-3 rounded-lg border',
                  isResolved ? 'bg-gray-800/30 border-gray-700' : severityColors[alert.severity]
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={clsx('px-2 py-0.5 text-xs rounded font-bold', severityBadgeColors[alert.severity])}>
                        {alert.type}
                      </span>
                      <span
                        className={clsx(
                          'px-2 py-0.5 text-xs rounded font-semibold',
                          isResolved
                            ? 'bg-green-600/20 text-green-400 border border-green-600'
                            : 'bg-yellow-600/20 text-yellow-400 border border-yellow-600'
                        )}
                      >
                        {isResolved ? '✓ Resolved' : alert.status}
                      </span>
                    </div>
                    <p className="text-white text-sm font-semibold mt-2">{alert.title}</p>
                    <p className="text-gray-400 text-xs mt-1">{alert.message}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <ClockIcon className="w-3 h-3" />
                      {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

        {/* Notifications (Messages) */}
        {notifications
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, 20)
          .map((notif) => {
            const typeColors: Record<string, string> = {
              USER_MESSAGE: 'bg-blue-500/10 border-blue-500',
              ALERT: 'bg-orange-500/10 border-orange-500',
              SOS: 'bg-red-500/10 border-red-500',
              SYSTEM: 'bg-gray-500/10 border-gray-500',
              TRACKING_ALERT: 'bg-green-500/10 border-green-500',
            };
            const typeBadgeColors: Record<string, string> = {
              USER_MESSAGE: 'bg-blue-500 text-white',
              ALERT: 'bg-orange-500 text-white',
              SOS: 'bg-red-500 text-white',
              SYSTEM: 'bg-gray-500 text-white',
              TRACKING_ALERT: 'bg-green-500 text-white',
            };

            return (
              <div
                key={notif.id}
                className={clsx(
                  'p-3 rounded-lg border',
                  notif.read ? 'bg-gray-800/30 border-gray-700' : typeColors[notif.type] || typeColors.SYSTEM
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx('px-2 py-0.5 text-xs rounded font-bold', typeBadgeColors[notif.type] || typeBadgeColors.SYSTEM)}
                      >
                        {notif.type === 'USER_MESSAGE' ? 'MSG' : notif.type === 'TRACKING_ALERT' ? '📍 TRACK' : notif.type}
                      </span>
                      {notif.read && (
                        <span className="px-2 py-0.5 text-xs rounded font-semibold bg-green-600/20 text-green-400 border border-green-600">
                          ✓ Read
                        </span>
                      )}
                    </div>
                    <p className="text-white text-sm font-semibold mt-2">{notif.title}</p>
                    <p className="text-gray-400 text-xs mt-1">{notif.message}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <ClockIcon className="w-3 h-3" />
                      {formatDistanceToNow(new Date(notif.timestamp), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

        {/* Geofence Events (Entry/Exit) */}
        {geofenceEvents.slice(0, 30).map((event: any) => {
          const isEntry = event.eventType === 'ENTRY';
          const eventColors: Record<string, string> = {
            ENTRY: 'bg-green-500/10 border-green-500',
            EXIT: 'bg-blue-500/10 border-blue-500',
          };
          const eventBadgeColors: Record<string, string> = {
            ENTRY: 'bg-green-500 text-white',
            EXIT: 'bg-blue-500 text-white',
          };

          return (
            <div
              key={event.id}
              className={clsx(
                'p-3 rounded-lg border',
                eventColors[event.eventType as 'ENTRY' | 'EXIT'] || 'bg-gray-800/30 border-gray-700'
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        'px-2 py-0.5 text-xs rounded font-bold',
                        eventBadgeColors[event.eventType as 'ENTRY' | 'EXIT'] || 'bg-gray-500 text-white'
                      )}
                    >
                      {event.eventType}
                    </span>
                    <span className="px-2 py-0.5 text-xs rounded font-semibold bg-gray-600/20 text-gray-300 border border-gray-600">
                      {event.geofence?.type || 'AREA'}
                    </span>
                  </div>
                  <p className="text-white text-sm font-semibold mt-2">{event.platoon?.callsign || 'Unknown'}</p>
                  <p className="text-gray-400 text-xs">
                    {isEntry ? 'Entered' : 'Exited'} {event.geofence?.name || 'Unknown Area'}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                    <ClockIcon className="w-3 h-3" />
                    {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {sosEvents.length === 0 && alerts.length === 0 && notifications.length === 0 && geofenceEvents.length === 0 && (
          <div className="text-center py-8">
            <SignalIcon className="w-12 h-12 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No alerts</p>
            <p className="text-gray-600 text-xs mt-1">All clear</p>
          </div>
        )}
      </div>
    </div>
  );
}
